import { useState } from "react";
import { isIOS } from "@/modules/orientationManager";
import { settleAfterRotate, SETTLE_MS } from "@/modules/rotateSettle";
import type { VideoJsSelf } from "@/components/player/videoJsTypes";

// After a rotate TO landscape on iOS Safari, the native control bar (play/pause)
// can end up stuck in a corner / behind the video until something forces a full
// layout+composite pass (a screenshot does). A permanent translateZ(0) made this
// worse — sticky compositor layer, wrong matrix. Instead we re-measure against
// the laid-out host across the rotate-settle window and force a real hide/show
// reflow on the bar (the closest equivalent to what a screenshot does). Gated to
// iOS + landscape; a no-op everywhere else.
function createLandscapeRecomposite(self: VideoJsSelf) {
  function recompositeLandscapeControls() {
    if (!isIOS() || typeof window === "undefined") {
      return;
    }
    const rootEl =
      self.player && !self.player.isDisposed() && self.player.el
        ? self.player.el()
        : null;
    if (
      !rootEl ||
      typeof rootEl.closest !== "function" ||
      !rootEl.closest(".experience-container--landscape")
    ) {
      return;
    }
    if (self.cancelCtrlRecomposite) {
      self.cancelCtrlRecomposite();
    }

    const getBarEl = () =>
      self.player && !self.player.isDisposed() && self.player.controlBar
        ? (self.player.controlBar.el() as HTMLElement)
        : null;

    // Sync the player's internal width/height to the laid-out `.video-panel`
    // (NOT `videoHost` — that node is `display: contents`, so clientWidth is
    // always 0). Template sizes can still be stale here on iOS; the panel's box
    // is what CSS actually painted. Then ask Video.js to re-layout and pin the
    // bar to the panel edges (clears any leftover transform that could park the
    // play button in a corner).
    const refresh = () => {
      if (!self.player || self.player.isDisposed()) {
        return;
      }
      const playerEl = self.player.el && self.player.el();
      const panelEl =
        (self.videoHost && self.videoHost.parentElement) ||
        (playerEl && playerEl.parentElement) ||
        null;
      // Kill stale inline margins on the panel (the VideoJs render still sets
      // them from the template; landscape CSS zeros them, but inline can win
      // until the next React commit — and after adopt that commit can lag).
      if (panelEl && panelEl.classList.contains("video-panel")) {
        panelEl.style.marginLeft = "0px";
        panelEl.style.marginTop = "0px";
        panelEl.style.marginRight = "0px";
        panelEl.style.marginBottom = "0px";
      }
      const w =
        (panelEl && panelEl.clientWidth) ||
        (playerEl && playerEl.clientWidth) ||
        0;
      const h =
        (panelEl && panelEl.clientHeight) ||
        (playerEl && playerEl.clientHeight) ||
        0;
      if (w > 0 && h > 0) {
        try {
          if (self.player.width() !== w || self.player.height() !== h) {
            self.player.dimensions(w, h);
          }
          self.player.trigger("playerresize");
        } catch {
          /* best-effort */
        }
      }
      const barEl = getBarEl();
      if (barEl) {
        barEl.style.transform = "";
        barEl.style.left = "0px";
        barEl.style.right = "0px";
        barEl.style.bottom = "0px";
        barEl.style.width = "100%";
      }
    };

    // Closest equivalent to the screenshot that fixes it live: drop the PLAYER
    // (not just the bar) out of paint for a frame so WebKit discards its wrong
    // compositor layers, then put it back so bar + tech re-layout together
    // against the settled landscape box.
    const forceRepaint = () => {
      const playerEl = (self.player &&
        self.player.el &&
        self.player.el()) as HTMLElement | null;
      const barEl = getBarEl();
      const target = playerEl || barEl;
      if (!target) {
        return;
      }
      const prev = target.style.visibility;
      target.style.visibility = "hidden";
      // Force layout while hidden.
      void target.offsetHeight;
      target.style.visibility = prev || "";
      if (barEl) {
        // Drop the temporary geometry pins so CSS owns them again.
        barEl.style.left = "";
        barEl.style.right = "";
        barEl.style.bottom = "";
        barEl.style.width = "";
      }
    };

    // Re-measure every frame while iOS's orientation values settle, then do one
    // hard repaint at the end (when the box is finally correct).
    let didFinal = false;
    self.cancelCtrlRecomposite = settleAfterRotate(() => {
      refresh();
    });
    // settleAfterRotate cancels itself after SETTLE_MS; schedule the hard
    // repaint just PAST that window (+ a small buffer) so it runs against
    // settled geometry. Derived from SETTLE_MS so the two can't drift apart if
    // the settle window is ever retuned.
    const FINAL_REPAINT_BUFFER_MS = 50;
    const finalTimer = window.setTimeout(() => {
      if (didFinal) {
        return;
      }
      didFinal = true;
      refresh();
      forceRepaint();
      self.cancelCtrlRecomposite = null;
    }, SETTLE_MS + FINAL_REPAINT_BUFFER_MS);
    const cancelSettle = self.cancelCtrlRecomposite;
    self.cancelCtrlRecomposite = () => {
      if (cancelSettle) {
        cancelSettle();
      }
      window.clearTimeout(finalTimer);
      const barEl = getBarEl();
      if (barEl) {
        barEl.style.transform = "";
        barEl.style.left = "";
        barEl.style.right = "";
        barEl.style.bottom = "";
        barEl.style.width = "";
        barEl.style.display = "";
      }
      self.cancelCtrlRecomposite = null;
    };
  }

  return { recompositeLandscapeControls };
}

export type LandscapeRecomposite = ReturnType<
  typeof createLandscapeRecomposite
>;

/** The iOS landscape control-bar recomposite of one VideoJs mount; stable for the mount. */
export function useLandscapeRecomposite(
  self: VideoJsSelf,
): LandscapeRecomposite {
  const [recomposite] = useState(() => createLandscapeRecomposite(self));
  return recomposite;
}
